/**
 * GroupMappingManagement - Admin UI for managing Azure AD group to role mappings
 * 
 * Provides CRUD operations for group mappings:
 * - List all group mappings in a table
 * - Add/Edit/Delete mapping forms
 * - Display Azure group ID, role, description
 * 
 * Requirements: 1.1, 1.3, 1.4
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/apiService';
import { UserRole } from '../contexts/AuthContext';

// GUID validation regex
const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// Role display names
const ROLE_DISPLAY_NAMES = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.HR_ADMIN]: 'HR Admin',
  [UserRole.SCHOOL_ADMIN]: 'School Admin',
  [UserRole.TEACHER]: 'Teacher',
  [UserRole.SUBSTITUTE]: 'Substitute',
  [UserRole.READONLY]: 'Read Only',
};

// Role descriptions
const ROLE_DESCRIPTIONS = {
  [UserRole.SUPER_ADMIN]: 'Full system access including configuration',
  [UserRole.HR_ADMIN]: 'HR operations and employee management',
  [UserRole.SCHOOL_ADMIN]: 'School-level administration',
  [UserRole.TEACHER]: 'Teacher access to observations and absences',
  [UserRole.SUBSTITUTE]: 'Limited substitute teacher access',
  [UserRole.READONLY]: 'View-only access to permitted areas',
};

const GroupMappingManagement = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [formData, setFormData] = useState({
    azure_group_id: '',
    azure_group_name: '',
    role: '',
    description: '',
    priority: 0,
  });
  const [formErrors, setFormErrors] = useState({});

  // Load mappings on mount
  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGroupMappings();
      setMappings(response.mappings || []);
    } catch (error) {
      toast.error('Failed to load group mappings');
      console.error('Error loading mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.azure_group_id) {
      errors.azure_group_id = 'Azure Group ID is required';
    } else if (!GUID_REGEX.test(formData.azure_group_id)) {
      errors.azure_group_id = 'Invalid GUID format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)';
    }

    if (!formData.role) {
      errors.role = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      setFormData({
        azure_group_id: mapping.azure_group_id,
        azure_group_name: mapping.azure_group_name || '',
        role: mapping.role,
        description: mapping.description || '',
        priority: mapping.priority || 0,
      });
    } else {
      setEditingMapping(null);
      setFormData({
        azure_group_id: '',
        azure_group_name: '',
        role: '',
        description: '',
        priority: 0,
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
    setFormData({
      azure_group_id: '',
      azure_group_name: '',
      role: '',
      description: '',
      priority: 0,
    });
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const payload = {
        azure_group_id: formData.azure_group_id.toLowerCase(),
        azure_group_name: formData.azure_group_name || null,
        role: formData.role,
        description: formData.description || null,
        priority: parseInt(formData.priority, 10) || 0,
      };

      if (editingMapping) {
        await apiService.updateGroupMapping(editingMapping.id, payload);
        toast.success('Group mapping updated successfully');
      } else {
        await apiService.createGroupMapping(payload);
        toast.success('Group mapping created successfully');
      }

      handleCloseDialog();
      await loadMappings();
    } catch (error) {
      const message = error.message || 'Failed to save group mapping';
      toast.error(message);
      console.error('Error saving mapping:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!mappingToDelete) return;

    try {
      setSaving(true);
      await apiService.deleteGroupMapping(mappingToDelete.id);
      toast.success('Group mapping deleted successfully');
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
      await loadMappings();
    } catch (error) {
      toast.error('Failed to delete group mapping');
      console.error('Error deleting mapping:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Azure AD Group Mappings
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Map Azure AD security groups to PeoplePulse roles
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadMappings}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Mapping
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                How Group Mappings Work
              </p>
              <p className="text-sm text-blue-700 mt-1">
                When users sign in with Azure AD, their group memberships are matched against
                these mappings to determine their PeoplePulse roles and permissions. Users
                belonging to multiple mapped groups will receive combined permissions from all
                matching roles.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configured Mappings ({mappings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {mappings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No group mappings configured</p>
              <p className="text-sm mt-1">
                Click "Add Mapping" to create your first Azure AD group mapping
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Azure Group</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {mapping.azure_group_name || 'Unnamed Group'}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">
                          {mapping.azure_group_id}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {ROLE_DISPLAY_NAMES[mapping.role] || mapping.role}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {mapping.description || '-'}
                    </TableCell>
                    <TableCell>{mapping.priority}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(mapping.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(mapping)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(mapping)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingMapping ? 'Edit Group Mapping' : 'Add Group Mapping'}
            </DialogTitle>
            <DialogDescription>
              {editingMapping
                ? 'Update the Azure AD group to role mapping'
                : 'Create a new mapping between an Azure AD group and a PeoplePulse role'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="azure_group_id">
                Azure AD Group ID <span className="text-red-500">*</span>
              </Label>
              <Input
                id="azure_group_id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={formData.azure_group_id}
                onChange={(e) =>
                  setFormData({ ...formData, azure_group_id: e.target.value })
                }
                className={formErrors.azure_group_id ? 'border-red-500' : ''}
              />
              {formErrors.azure_group_id && (
                <p className="text-sm text-red-500">{formErrors.azure_group_id}</p>
              )}
              <p className="text-xs text-gray-500">
                The Object ID (GUID) of the Azure AD security group
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="azure_group_name">Group Display Name</Label>
              <Input
                id="azure_group_name"
                placeholder="e.g., PeoplePulse-HR-Admins"
                value={formData.azure_group_name}
                onChange={(e) =>
                  setFormData({ ...formData, azure_group_name: e.target.value })
                }
              />
              <p className="text-xs text-gray-500">
                Optional friendly name for reference
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">
                PeoplePulse Role <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className={formErrors.role ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_DISPLAY_NAMES).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      <div>
                        <span>{label}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          - {ROLE_DESCRIPTIONS[value]}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.role && (
                <p className="text-sm text-red-500">{formErrors.role}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description of this mapping"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 0 })
                }
              />
              <p className="text-xs text-gray-500">
                Higher priority mappings take precedence in conflict resolution
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingMapping ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group Mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this group mapping?
              {mappingToDelete && (
                <div className="mt-2 p-3 bg-gray-100 rounded-md">
                  <p className="font-medium">
                    {mappingToDelete.azure_group_name || 'Unnamed Group'}
                  </p>
                  <p className="text-xs font-mono text-gray-500">
                    {mappingToDelete.azure_group_id}
                  </p>
                  <p className="text-sm mt-1">
                    Role: {ROLE_DISPLAY_NAMES[mappingToDelete.role] || mappingToDelete.role}
                  </p>
                </div>
              )}
              <p className="mt-2 text-amber-600">
                Users in this Azure AD group will no longer receive the associated role.
                This action will be logged in the audit trail.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GroupMappingManagement;
